import dayjs from 'dayjs'
import { Advertisement } from 'src/models/Advertisement'
import { Article } from 'src/models/Article'
import { TimelineType } from 'src/models/Timeline'
import { NewUser, RequestedUser } from 'src/models/User'
import { PromisedType } from 'src/utils/types'
import useSWR, { SWRConfiguration } from 'swr'
import { ApiClient, fetcher } from './client'
import { toContribution } from './contribution'
import {
  GET_MY_PAGE_DATA,
  GET_TIMELINE,
  POST_MY_PAGE_ACTION_LOG,
} from './endpoints'
import { toEvent } from './event'
import { toPremiumContribution } from './premiumContribution'
import { toRecommendedUser } from './recommendation'
import { toRoomOrGroup } from './room/converters'
import {
  CreateMyPageActionLogType,
  MyPageData,
  NewCompany,
  RequestedCompany,
  ServerAdvertisement,
  Article as ServerArticle,
  ServerTimeline,
} from './types/mypage'

const toNewUser = (data: NewCompany): NewUser => {
  // typeOfBusinessが文字列の場合は配列に変換、配列の場合はそのまま使用
  const typeOfBusinessArray = Array.isArray(data.typeOfBusiness)
    ? data.typeOfBusiness
    : data.typeOfBusiness
    ? [data.typeOfBusiness]
    : null

  return {
    id: data.id,
    roomUrl: data.roomUrl,
    isActive: true || data.isActive,
    isNew: data.isNew,
    isCertified: data.isCertified,
    name: data.adminName || '',
    companyName: data.name || '',
    position: data.position || '',
    capital: data.capitalText || '',
    businessType: typeOfBusinessArray?.join('、') || '',
    foundedOn: data.foundedAt ? `${dayjs(data.foundedAt).year()}` : undefined,
    imageSrc:
      typeof data.imageUrl === 'string'
        ? data.imageUrl
        : data.imageUrl?.medium?.url || data.imageUrl?.url,
    numberOfEmployees: data.employeeNumberText,
    prefecture: data.prefecture,
    adminAges: data.adminAges,
    typeOfBusiness: typeOfBusinessArray,
  }
}

const toArticle = (data: ServerArticle): Article => ({
  id: data.id,
  title: data.title,
  content: data.content,
  createdAt: dayjs(data.createdAt),
  updatedAt: dayjs(data.updatedAt),
})

const myPageDataFetcher = async (url: string) => {
  const res: MyPageData = await fetcher(url)
  return {
    newUsers: res.newCompanies.map(toNewUser),
    articles: res.articles.map(toArticle),
    popUp: res.popUp,
    modalArticle: res.modalArticle,
    advertisements: res.advertisements.map(toAdvertisement),
    events: res.events.map(toEvent),
  }
}

export const useMyPageData = (config?: Partial<SWRConfiguration>) =>
  useSWR<PromisedType<typeof myPageDataFetcher>>(
    GET_MY_PAGE_DATA,
    myPageDataFetcher,
    config,
  )

export const useTimeline = (userId: number) =>
  useSWR<TimelineType[]>([GET_TIMELINE, userId], timelineFetcher)

const timelineFetcher = async (
  url: string,
  userId: number,
): Promise<TimelineType[]> => {
  const res = await ApiClient.get<ServerTimeline>(url)
  if (res.error) throw res.error
  return (res.data?.data || [])
    .map((each) => {
      try {
        const date = dayjs(each.date).format('YYYY/MM/DD HH:mm')
        switch (each.type) {
          case 'room':
            return {
              date: date,
              type: 'room' as const,
              value: toRoomOrGroup(each.value, userId),
            }
          case 'requested_company':
            return {
              date: date,
              type: 'requestedUser' as const,
              value: toRequestedUser(each.value),
            }
          case 'premium_contribution':
            return {
              date: date,
              type: 'premiumContribution' as const,
              value: toPremiumContribution(each.value),
            }
          case 'contribution':
            return {
              date: date,
              type: 'contribution' as const,
              value: toContribution(each.value),
            }
          case 'recommend_company':
            return {
              date: date,
              type: 'recommendedUser' as const,
              value: toRecommendedUser(each.value),
            }
          case 'event':
            return {
              date: date,
              type: 'event' as const,
              value: toEvent(each.value),
            }
          case 'new_company':
            return {
              date: date,
              type: 'newUser' as const,
              value: toNewUser(each.value),
            }
          default:
            return {
              date: '',
              type: 'undefinedValue' as const,
              value: undefined,
            }
        }
      } catch (error) {
        console.error('Timeline item conversion error:', error, each)
        // エラーが発生したアイテムはスキップして、undefinedを返す
        // 後でfilterで除外される
        return {
          date: '',
          type: 'undefinedValue' as const,
          value: undefined,
        }
      }
    })
    .filter(
      (item) => item.type !== 'undefinedValue' && item.value !== undefined,
    )
}

const toRequestedUser = (data: RequestedCompany): RequestedUser => ({
  id: data.id,
  roomUrl: data.room.url,
  requestAt: dayjs(data.requestTime),
  requestReasonText: data.requestReasonText,
  imageSrc:
    typeof data.imageUrl === 'string'
      ? data.imageUrl
      : data.imageUrl?.medium?.url || data.imageUrl?.url || '',
  name: data.adminName,
  companyName: data.name,
  position: data.position || '',
  feedbackScore: data.feedbackScore,
  feedbackCount: data.feedbackCount,
})

export const toAdvertisement = (data: ServerAdvertisement): Advertisement => ({
  id: data.id,
  imageSource: data.imageSource,
  url: data.url,
})

export const createMyPageActionLog = async (params: {
  component: string
  item: string
  targetCompanyId?: number
  targetObjectId?: number
  memo?: string
}) => {
  const body: CreateMyPageActionLogType = {
    component: params.component,
    item: params.item,
    target_company_id: params.targetCompanyId,
    target_object_id: params.targetObjectId,
    memo: params.memo,
  }
  return ApiClient.post(POST_MY_PAGE_ACTION_LOG, body)
}
